/* cp-portal.js — Clinician Portal app machinery (Page Studio shared asset).
 *
 * The ps-cp-* section family renders ALL portal content as static, editable
 * Liquid (every tab stacked top-to-bottom — which is exactly what the Studio
 * editor canvas shows). This script flips the page into the tabbed portal
 * app on the live storefront:
 *   - tab navigation + hash deep links (#dashboard/#resources/#order/…)
 *   - Shopify-customer auth handshake with pelvicdashboard.com
 *   - personalized discount-code banner, stats, profile, recents
 *   - Patient Resources grid + FAQs (fetched from the dashboard API)
 *   - resource share menus, image preview modal, video modal, toasts
 *   - Order Materials cart, steppers, address prefill, draft-order submit
 *   - Product Info detail views (page embeds + legacy detail fallback)
 *
 * DOM contract (rendered by the sections):
 *   [data-cp-shell]                 shell root; data-cp-email set when the
 *                                   logged-in customer has the clinician tag
 *   [data-cp-app]                   shell topbar+nav wrapper (auth-gated)
 *   [data-cp-gate] / [data-cp-loader] / [data-cp-gate-error]
 *   [data-cp-nav="<slug>"]          nav tab buttons
 *   [data-cp-sec="<slug>"]          tab section roots (may be several/slug)
 *   [data-cp-goto="<slug>"]         any element that navigates to a tab
 *   dashboard: [data-cp-banner] [data-cp-welcome-name] [data-cp-stats]
 *              [data-cp-recent] [data-cp-last-ordered]
 *   resources: [data-cp-filter="all|<cat>"] [data-cp-res-search]
 *              [data-cp-res-grid]
 *   videos:    [data-cp-videos data-cp-initial=N] > [data-cp-vid-grid] >
 *              [data-cp-video data-vid data-title] cards, [data-cp-vid-more]
 *   order:     [data-cp-order-item data-item-id data-variant-id data-max
 *              data-qty-mult data-item-type] cards with [data-cp-qty="±1"] +
 *              [data-cp-qty-val]; [data-cp-order-gate] [data-cp-addr=*]
 *              [data-cp-order-btn] [data-cp-order-msg] [data-cp-faqs];
 *              [data-cp-order-summary] > [data-cp-sum-items] + [data-cp-sum-eta]
 *              (panel revealed via .cp-sum--on once any qty > 0)
 *   products:  [data-cp-product data-key data-embed …] cards,
 *              [data-cp-prod-list] [data-cp-prod-detail] [data-cp-embed-host]
 *   profile:   [data-cp-profile] [data-cp-profile-info] [data-cp-signout]
 *              [data-cp-account]
 *
 * Without this script the page reads perfectly top-to-bottom (that static
 * render IS the editor canvas). Everything here is progressive enhancement.
 */
(function () {
  'use strict';
  if (window.__cpPortal) return;
  window.__cpPortal = true;

  var API = 'https://pelvicdashboard.com/api/clinician-portal';
  /* Tab slugs come from the shell's nav buttons (editable in Page Studio) —
   * never hardcode them, or newly-added tabs would be unroutable. */
  function SECTIONS() {
    return $all('[data-cp-nav]').map(function (e) { return e.getAttribute('data-cp-nav'); })
      .filter(Boolean);
  }
  function defaultTab() { return SECTIONS()[0] || 'dashboard'; }

  var shell = null, EMAIL = null, token = null, isLoggedIn = false, data = null;
  /* Signed in to Shopify without the clinician tag (wrong email, or never
   * tagged). The shell renders [data-cp-wrong]; this state must never fall
   * through to the guest path — a Sign in link would bounce right back. */
  var WRONG = null;
  function wrongAccount() { return !!WRONG; }
  function switchHref() {
    // Sign out → Shopify sign-in screen → back to this page once signed in.
    var login = '/customer_authentication/login?return_to=' + encodeURIComponent(window.location.pathname + window.location.search);
    return '/account/logout?return_url=' + encodeURIComponent(login);
  }
  function stashTab() { try { sessionStorage.setItem('cp_return_tab', (window.location.hash || '').replace('#', '')); } catch (e) {} }
  function wireSwitchLinks() {
    $all('[data-cp-switch]').forEach(function (a) {
      a.setAttribute('href', switchHref());
      a.onclick = stashTab;
    });
  }
  var ORDER_SIGNIN_TEXT = 'Sign in to add items to your cart';
  function orderSigninHeading() {
    return '<p style="color:var(--ink);margin:0 0 6px;font-weight:700;font-size:1.1em;">' + ORDER_SIGNIN_TEXT + '</p>';
  }
  function wrongAccountCta(tab) {
    var email = WRONG.getAttribute('data-cp-wrong-email') || '';
    var reqUrl = WRONG.getAttribute('data-cp-request-url') || '/pages/clinicianportal#signup';
    var text = (WRONG.querySelector('.cp-wrong__text') || {}).textContent || ('You\'re signed in as ' + email + ', but this account isn\'t set up for the clinician portal.');
    var sw = (WRONG.querySelector('.cp-wrong__switch') || {}).textContent || 'Switch account';
    var rq = (WRONG.querySelector('.cp-wrong__request') || {}).textContent || 'Request access';
    // Order tab: the same heading for signed-out and wrong-account visitors,
    // the state's own sentence underneath (Saul, 2026-10-22).
    return (tab === 'order' ? orderSigninHeading() : '')
      + '<p style="color:var(--ink);margin:0 0 12px;">' + text + '</p>'
      + '<a href="' + switchHref() + '" onclick="try{sessionStorage.setItem(\'cp_return_tab\',\'' + tab + '\')}catch(e){}" style="display:inline-block;padding:10px 24px;margin:4px;background:var(--btn-bg);color:var(--ink);border-radius:8px;font-weight:600;text-decoration:none;">' + sw + '</a>'
      + '<a href="' + reqUrl + '" style="display:inline-block;padding:10px 24px;margin:4px;border:1px solid var(--ink);color:var(--ink);border-radius:8px;font-weight:600;text-decoration:none;">' + rq + '</a>';
  }
  /* Tell the dashboard so the team can see who is hitting this wall (once per
   * browser session per email; the server dedupes per day and posts to Slack). */
  function pingWrongAccount() {
    var email = WRONG.getAttribute('data-cp-wrong-email') || '';
    if (!email) return;
    var key = 'cp_wrong_pinged:' + email;
    try { if (sessionStorage.getItem(key)) return; sessionStorage.setItem(key, '1'); } catch (e) {}
    try {
      fetch(API + '/wrong-account', { method: 'POST', headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({ email: email, path: window.location.pathname }), keepalive: true }).catch(function () {});
    } catch (e) {}
  }
  var cart = {};
  var allResources = [];

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /* ─────────────────────── JS-owned UI (modals + toast) ─────────────────── */
  // Machinery UI is created here, not in section markup, so the editor canvas
  // never shows empty modal scaffolding and ids can't collide.
  function buildChrome() {
    var host = document.createElement('div');
    host.className = 'cp';
    host.innerHTML =
      '<div class="cp-vid-modal" id="cp-vid-modal">' +
      '<div class="cp-vid-modal__inner">' +
      '<div class="cp-vid-modal__player" id="cp-vid-player"></div>' +
      '<div class="cp-vid-modal__footer">' +
      '<div class="cp-vid-modal__title" id="cp-vid-modal-title"></div>' +
      '<div class="cp-vid-modal__actions">' +
      '<button class="cp-vid-modal__btn cp-vid-modal__copy" id="cp-vid-modal-share" onclick="cpShareVideo()">' +
      '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>' +
      ' Copy link</button>' +
      '<button class="cp-vid-modal__btn cp-vid-modal__close" onclick="cpCloseVideo()">Close</button>' +
      '</div></div></div></div>' +
      '<div class="cp-preview-modal" id="cp-preview-modal">' +
      '<div class="cp-preview-modal__frame">' +
      '<button class="cp-preview-modal__close" onclick="cpPreviewClose()" aria-label="Close">' +
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
      '</button>' +
      '<img id="cp-preview-img" class="cp-preview-modal__img" src="" alt="Preview">' +
      '</div></div>' +
      '<div class="cp-toast" id="cp-toast"></div>';
    document.body.appendChild(host);
    $('#cp-vid-modal').addEventListener('click', function (e) { cpCloseVideo(e); });
    $('.cp-vid-modal__inner', host).addEventListener('click', function (e) { e.stopPropagation(); });
    $('#cp-preview-modal').addEventListener('click', function (e) { if (e.target === e.currentTarget) cpPreviewClose(); });
  }

  /* ─────────────────────── Fetch helper ─────────────────────── */
  function api(path, opts) {
    var h = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = 'Bearer ' + token;
    opts = opts || {};
    opts.headers = h;
    return fetch(API + path, opts).then(function (r) {
      return r.json().then(function (d) {
        if (!r.ok) throw new Error(d.error || 'Request failed');
        return d;
      });
    });
  }

  /* ─────────────────────── Auth ─────────────────────── */
  /* Store-signed sign-in: the shell carries the store's signature for this customer
     (integrations/portal_signin.py). A refused one (an old page) reloads once for a fresh
     signature; the sessionStorage flag stops a reload loop. */
  function signedBody() {
    var b = { email: EMAIL };
    var cid = shell && shell.getAttribute('data-cp-cid'),
        ts = shell && shell.getAttribute('data-cp-ts'),
        sig = shell && shell.getAttribute('data-cp-sig');
    if (cid && ts && sig) { b.customer_id = cid; b.ts = ts; b.sig = sig; }
    return b;
  }
  function authShopify() {
    fetch(API + '/auth/shopify', { method: 'POST', headers: { 'Content-Type': 'application/json' },
                                   body: JSON.stringify(signedBody()) })
      .then(function (r) {
        return r.json().then(function (d) {
          if (!r.ok) {
            var once = false;
            try { once = sessionStorage.getItem('cp_signin_reload') === '1'; } catch (x) {}
            if (d && d.reload && !once) {
              try { sessionStorage.setItem('cp_signin_reload', '1'); } catch (x) {}
              window.location.reload();
              return new Promise(function () {});
            }
            throw new Error(d.error || 'Request failed');
          }
          try { sessionStorage.removeItem('cp_signin_reload'); } catch (x) {}
          return d;
        });
      })
      .then(function (d) {
        token = d.token;
        data = d.clinician;
        isLoggedIn = true;
        localStorage.setItem('cp_token', token);
        try { localStorage.setItem('cp_token_email', (EMAIL || '').toLowerCase()); } catch (x) {}
        show();
      })
      .catch(function (e) {
        var el = $('[data-cp-gate-error]');
        if (el) { el.textContent = e.message; el.classList.remove('cp-hidden'); }
        var ld = $('[data-cp-loader]');
        if (ld) ld.classList.add('cp-hidden');
      });
  }

  window.cpSignOut = function () {
    token = null; data = null;
    localStorage.removeItem('cp_token');
    try { localStorage.removeItem('cp_token_email'); } catch (x) {}
    window.location.href = '/account/logout';
  };

  function toggleProfile() {
    var el = $('[data-cp-profile]');
    if (!el) return;
    // Re-parent to <body>: the theme's <main> is a stacking context (z-index 1)
    // below .header-group (z-index 3), so no z-index inside it can clear the
    // header. The .cp class carries the design tokens along.
    if (el.parentElement !== document.body) { el.classList.add('cp'); document.body.appendChild(el); }
    el.classList.toggle('cp-hidden');
  }

  /* ─────────────────────── Show portal ─────────────────────── */
  function show() {
    var gate = $('[data-cp-gate]');
    if (gate) gate.classList.add('cp-hidden');
    var app = $('[data-cp-app]');
    if (app) app.classList.remove('cp-hidden');
    $all('[data-cp-sec]').forEach(function (el) { el.classList.remove('cp-hidden'); });

    if (!data && token) {
      api('/me').then(function (d) { data = d; isLoggedIn = true; render(); })
        .catch(function () {
          token = null; localStorage.removeItem('cp_token');
          if (EMAIL) { authShopify(); } else { render(); }
        });
    } else {
      render();
    }
  }

  function render() {
    renderDashboard();
    renderOrder();
    renderProfile();

    var vp = new URLSearchParams(window.location.search).get('video');
    var rt = null;
    try { rt = sessionStorage.getItem('cp_return_tab'); sessionStorage.removeItem('cp_return_tab'); } catch (e) {}
    if (vp) {
      // ?video= deep link → whichever tab holds a videos section
      var vidSec = $('[data-cp-videos]');
      var vidTab = vidSec && vidSec.getAttribute('data-cp-sec');
      cpNav(vidTab || defaultTab(), true);
    } else if (rt && SECTIONS().indexOf(rt) !== -1) {
      cpNav(rt, true);
    } else {
      cpNavFromHash();
    }
  }

  function cpNavFromHash() {
    var slug = (window.location.hash || '').replace('#', '');
    if (slug && SECTIONS().indexOf(slug) !== -1) cpNav(slug, true);
    else cpNav(defaultTab(), true);
  }
  // Browser back/forward walks the tab history (see cpNav's pushState)
  window.addEventListener('hashchange', cpNavFromHash);

  /* ─────────────────────── Navigation ─────────────────────── */
  // cpNav(slug)          — user navigation: PUSHES a history entry so the
  //                        browser back button returns to the previous tab
  //                        instead of leaving the portal
  // cpNav(slug, true)    — initial load / hash sync: replaces the entry
  function cpNav(s, replace) {
    if (window.location.hash !== '#' + s) {
      var url = window.location.pathname + window.location.search + '#' + s;
      if (replace && history.replaceState) history.replaceState(null, '', url);
      else if (!replace && history.pushState) history.pushState(null, '', url);
    }
    $all('[data-cp-nav]').forEach(function (e) {
      e.classList.toggle('cp-nav__tab--active', e.getAttribute('data-cp-nav') === s);
    });
    $all('[data-cp-sec]').forEach(function (el) {
      el.classList.toggle('cp-tab-off', el.getAttribute('data-cp-sec') !== s);
    });
    // Non-portal sections assigned to a tab in Page Studio: each nav button
    // carries their template-JSON section keys; toggle their theme wrappers.
    $all('[data-cp-nav]').forEach(function (e) {
      var on = e.getAttribute('data-cp-nav') === s;
      (e.getAttribute('data-cp-keys') || '').split(',').forEach(function (k) {
        k = k.trim();
        if (!k) return;
        var el = $('.shopify-section[id$="__' + k + '"]');
        if (el && !el.querySelector('[data-cp-sec]')) el.classList.toggle('cp-tab-off', !on);
      });
    });
    // Content loads key off what the now-visible tab CONTAINS, not the slug —
    // tabs are renamable/movable in Page Studio.
    var visible = $all('[data-cp-sec="' + s + '"]');
    var has = function (sel) {
      return visible.some(function (el) { return el.matches(sel) || !!el.querySelector(sel); });
    };
    if (has('[data-cp-res-grid], [data-cp-videos]')) loadResources();
    if (has('[data-cp-faqs]')) loadFaqs();
    if (has('[data-cp-prod-list]')) {
      $all('[data-cp-prod-list]').forEach(function (el) { el.classList.remove('cp-hidden'); });
      $all('[data-cp-prod-detail]').forEach(function (el) { el.classList.add('cp-hidden'); });
    }
    var emb = $('[data-cp-embed-host]');
    if (emb) emb.classList.add('cp-hidden');
    _cpTopbarBack(true);
    window.scrollTo(0, 0);
  }
  window.cpNav = cpNav; // kept global for JS-rendered fragments

  /* ═══ DASHBOARD ═══ */
  function renderDashboard() {
    var nameEl = $('[data-cp-welcome-name]');
    var bannerEl = $('[data-cp-banner]');
    var statsEl = $('[data-cp-stats]');
    var loEl = $('[data-cp-last-ordered]');
    var rrEl = $('[data-cp-recent]');
    var dashBottom = rrEl ? rrEl.closest('.cp-dash-bottom') : null;
    var dashTop = dashBottom && dashBottom.parentElement ? dashBottom.parentElement.querySelector('.cp-dash-top') : null;
    if (!isLoggedIn) {
      if (nameEl && nameEl.getAttribute('data-guest-text')) nameEl.textContent = nameEl.getAttribute('data-guest-text');
      if (bannerEl) bannerEl.innerHTML = '';
      if (statsEl) statsEl.innerHTML = '';
      if (dashBottom) dashBottom.style.display = 'none';
      else if (loEl && loEl.parentElement) loEl.parentElement.style.display = 'none';
      if (dashTop) dashTop.style.marginBottom = '0';
      return;
    }
    if (dashTop) dashTop.style.marginBottom = '';
    // Cards row only appears once there's something to show — a favorited/
    // viewed resource or a past order. Both empty = hide the whole row.
    var viewedRes = [];
    try { viewedRes = JSON.parse(localStorage.getItem('cp_viewed_resources') || '[]'); } catch (e) {}
    var hasCardData = viewedRes.length > 0 || (data.recent_orders || []).length > 0;
    if (dashBottom) dashBottom.style.display = hasCardData ? '' : 'none';

    var p = data.profile, s = data.stats;
    var firstName = (p.name || 'there').split(' ')[0];
    if (nameEl) nameEl.textContent = 'Welcome, ' + firstName;

    window.CP_CODE = data.personal_code || null;
    var woohoo = (data.codes && data.codes.length) ? data.codes[0] : null;
    if (bannerEl && woohoo) {
      bannerEl.innerHTML = '<div class="cp-code-banner">'
        + '<div class="cp-code-banner__left">'
        + '<div class="cp-code-banner__icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg></div>'
        + '<span class="cp-code-banner__text">Patient discount: <span class="cp-code-banner__code">' + woohoo.code + '</span> · ' + woohoo.discount + '</span>'
        + '</div>'
        + '<button class="cp-code-banner__copy" onclick="cpCopy(this,\'' + woohoo.code + '\')">Copy</button>'
        + '</div>';
    }

    if (statsEl) {
      var sh = '';
      if (s.state_clinician_count > 0 && p.state)
        sh += '<div class="cp-stat"><div class="cp-stat__val">' + s.state_clinician_count + '</div><div class="cp-stat__label">Clinicians in ' + p.state + '</div></div>';
      if (s.postcards_this_quarter > 0)
        sh += '<div class="cp-stat"><div class="cp-stat__val">' + s.postcards_this_quarter + '+</div><div class="cp-stat__label">Postcards shipped this quarter</div></div>';
      sh += '<div class="cp-stat"><div class="cp-stat__val">' + (s.total_clinicians || '10,000') + '+</div><div class="cp-stat__label">Vetted Clinicians in the collective</div></div>';
      statsEl.innerHTML = sh;
    }

    var orders = data.recent_orders || [];
    if (loEl) {
      var loEmpty = cpEmptyState(loEl, 'No orders yet. Order free postcards and samples.');
      if (loEl.parentElement) loEl.parentElement.style.display = '';
      if (!orders.length) {
        loEl.innerHTML = loEmpty;
      } else {
        var oh = '';
        orders.slice(0, 3).forEach(function (o) {
          var dt = new Date(o.created_at + 'Z');
          var ds = dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          oh += '<div class="cp-dash-item">'
            + '<div class="cp-dash-item__icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg></div>'
            + '<div class="cp-dash-item__info">'
            + '<div class="cp-dash-item__name">' + (o.shopify_order_number || 'Order') + '</div>'
            + '<div class="cp-dash-item__meta">' + o.order_type + ' · ' + ds + '</div>'
            + '</div></div>';
        });
        loEl.innerHTML = oh;
      }
    }

    renderRecentResources();
  }

  // The Liquid-rendered empty state is editable in Page Studio — capture it on
  // first sight and reuse it, instead of overwriting with a hardcoded string.
  function cpEmptyState(el, fallback) {
    if (el.__cpEmpty === undefined) {
      var e = el.querySelector('.cp-dash-card__empty');
      el.__cpEmpty = e ? e.outerHTML : '<div class="cp-dash-card__empty">' + fallback + '</div>';
    }
    return el.__cpEmpty;
  }

  function renderRecentResources() {
    var rrEl = $('[data-cp-recent]');
    if (!rrEl) return;
    var rrEmpty = cpEmptyState(rrEl, 'Download a resource to see it here');
    var viewed = [];
    try { viewed = JSON.parse(localStorage.getItem('cp_viewed_resources') || '[]'); } catch (e) {}
    // Keep the cards row's visibility in sync — a first download while on
    // the dashboard should reveal the row without a reload.
    if (isLoggedIn) {
      var rrDash = rrEl.closest('.cp-dash-bottom');
      if (rrDash) rrDash.style.display = (viewed.length || (data && data.recent_orders || []).length) ? '' : 'none';
    }
    if (!viewed.length) {
      rrEl.innerHTML = rrEmpty;
    } else {
      var rh = '';
      viewed.slice(0, 3).forEach(function (r) {
        rh += '<div class="cp-dash-item">'
          + '<div class="cp-dash-item__icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></div>'
          + '<div class="cp-dash-item__info">'
          + '<div class="cp-dash-item__name">' + r.title + '</div>'
          + '<div class="cp-dash-item__meta">' + r.format + '</div>'
          + '</div>'
          + '<button class="cp-dash-item__btn" onclick="cpDl(\'' + r.id + '\',\'' + r.title.replace(/'/g, "\\'") + '\',\'' + r.format + '\')">Download</button>'
          + '</div>';
      });
      rrEl.innerHTML = rh;
    }
  }

  window.cpCopy = function (btn, code) {
    navigator.clipboard.writeText(code).then(function () {
      btn.textContent = 'Copied';
      btn.classList.add('cp-code-banner__copy--done');
      setTimeout(function () { btn.textContent = 'Copy'; btn.classList.remove('cp-code-banner__copy--done'); }, 2000);
    });
  };

  /* ═══ PATIENT RESOURCES ═══ */
  var resLoaded = false;
  var activeFilters = [];

  function loadResources() {
    initVideos();
    if (resLoaded) { cpFilterResources(); return; }
    api('/materials').then(function (res) {
      allResources = res.materials || [];
      renderResources(allResources);
      resLoaded = true;
    }).catch(function (e) { console.error('Resources load failed:', e); });
    loadFaqs();
  }

  var faqsLoaded = false;
  function loadFaqs() {
    if (faqsLoaded) return;
    var host = $('[data-cp-faqs]');
    if (!host) { faqsLoaded = true; return; }
    api('/faq').then(function (res) {
      var faqs = res.faq || [];
      var fh = '';
      faqs.forEach(function (f, i) {
        fh += '<div class="cp-faq" id="faq' + i + '">'
          + '<button class="cp-faq__q" onclick="cpFaq(' + i + ')">'
          + f.question
          + '<svg class="cp-faq__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>'
          + '</button>'
          + '<div class="cp-faq__a">' + f.answer + '</div></div>';
      });
      host.innerHTML = fh;
      faqsLoaded = true;
    }).catch(function (e) { console.error('FAQ load failed:', e); });
  }
  window.cpFaq = function (i) { document.getElementById('faq' + i).classList.toggle('cp-faq--open'); };

  function renderResources(mats) {
    var gridEl = $('[data-cp-res-grid]');
    if (!gridEl) return;
    if (!mats.length) {
      gridEl.innerHTML = '<div class="cp-res-empty">No resources found</div>';
      return;
    }
    var html = '';
    mats.forEach(function (m) {
      var isImg = m.format === 'PNG' || m.format === 'JPG' || m.format === 'JPEG';
      var fileUrl = cpMaterialUrl(m.id);
      var safeTitle = m.title.replace(/"/g, '&quot;');
      var thumbContent = (isImg && m.available)
        ? '<img src="' + fileUrl + '" alt="' + safeTitle + '" loading="lazy">'
        : (m.format === 'PDF'
          ? '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>'
          : '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>');
      var titleJsEsc = m.title.replace(/\\/g, '\\\\').replace(/'/g, "\\'");

      html += '<div class="cp-res-card" data-title="' + m.title.toLowerCase() + '" data-tags="' + (m.tags || []).join(',').toLowerCase() + '">'
        + '<div class="cp-res-card__thumb" ' + (m.available ? 'onclick="cpPreviewRes(\'' + m.id + '\',\'' + titleJsEsc + '\')"' : '') + '>' + thumbContent + '</div>'
        + '<div class="cp-res-card__body">'
        + '<div class="cp-res-card__title">' + m.title + '</div>'
        + '<div class="cp-res-card__desc">' + m.description + '</div>'
        + '<button class="cp-res-card__preview" ' + (m.available ? 'onclick="cpPreviewRes(\'' + m.id + '\',\'' + titleJsEsc + '\')"' : 'disabled') + '>'
        + (m.available ? 'Preview' : 'Coming soon') + '</button>'
        + '<div class="cp-res-card__actions">'
        + '<button class="cp-res-card__action" ' + (m.available ? 'onclick="cpDl(\'' + m.id + '\',\'' + m.title.replace(/'/g, "\\'") + '\',\'' + m.format + '\')"' : 'disabled') + '>'
        + '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>'
        + 'Download</button>'
        + '<div class="cp-share-wrap">'
        + '<button class="cp-res-card__action" ' + (m.available ? 'onclick="cpShareToggle(event, \'' + m.id + '\', this)"' : 'disabled') + ' aria-haspopup="menu" aria-expanded="false">'
        + '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v13"/><polyline points="7 8 12 3 17 8"/><path d="M20 12v7a2 2 0 01-2 2H6a2 2 0 01-2-2v-7"/></svg>'
        + 'Share</button>'
        + '<div class="cp-share-menu" role="menu" data-title="' + m.title.replace(/"/g, '&quot;') + '">'
        + '<button class="cp-share-menu__item" role="menuitem" onclick="cpShareCopy(event, this)">'
        + '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>'
        + 'Copy link</button>'
        + '<button class="cp-share-menu__item" role="menuitem" onclick="cpShareEmail(event, this)">'
        + '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>'
        + 'Email link</button>'
        + '</div>'
        + '</div>'
        + '</div></div></div>';
    });
    gridEl.innerHTML = html;
  }

  function toggleFilter(cat, tagEl) {
    var key = cat.toLowerCase();
    var on = activeFilters.indexOf(key) === -1;
    if (on) activeFilters.push(key);
    else activeFilters = activeFilters.filter(function (f) { return f !== key; });
    tagEl.classList.toggle('cp-filter-tag--active', on);
    var allEl = $('[data-cp-filter="all"]');
    if (allEl) allEl.classList.toggle('cp-filter-tag--active', activeFilters.length === 0);
    cpFilterResources();
  }

  function selectAllFilters() {
    activeFilters = [];
    $all('[data-cp-filter]').forEach(function (el) {
      el.classList.toggle('cp-filter-tag--active', el.getAttribute('data-cp-filter') === 'all');
    });
    cpFilterResources();
  }

  function cpFilterResources() {
    var input = $('[data-cp-res-search]');
    var search = ((input && input.value) || '').toLowerCase();
    var filtered = allResources.filter(function (m) {
      var matchSearch = !search || m.title.toLowerCase().indexOf(search) !== -1 || m.description.toLowerCase().indexOf(search) !== -1;
      var matchTag = !activeFilters.length || activeFilters.some(function (f) {
        return (m.tags || []).some(function (t) { return t.toLowerCase().indexOf(f) !== -1; })
          || m.title.toLowerCase().indexOf(f) !== -1
          || m.description.toLowerCase().indexOf(f) !== -1;
      });
      return matchSearch && matchTag;
    });
    renderResources(filtered);
  }
  window.cpFilterResources = cpFilterResources;

  window.cpPreviewRes = function (id, title) {
    var modal = document.getElementById('cp-preview-modal');
    var img = document.getElementById('cp-preview-img');
    if (!modal || !img) return;
    img.src = cpMaterialUrl(id);
    img.alt = title || 'Preview';
    modal.classList.add('cp-preview-modal--open');
    document.body.style.overflow = 'hidden';
  };
  window.cpPreviewClose = function () {
    var modal = document.getElementById('cp-preview-modal');
    if (modal) modal.classList.remove('cp-preview-modal--open');
    document.body.style.overflow = '';
  };

  var CP_SHEET_MAP = { 'clinician-kiwi': 'clinician_kiwi', 'clinician-ohnut': 'clinician_ohnut', 'patient-kiwi': 'patient_kiwi', 'patient-ohnut': 'patient_ohnut' };
  function cpMaterialUrl(id) {
    if (window.CP_CODE && CP_SHEET_MAP[id])
      return 'https://pelvicdashboard.com/api/clinician-sheets/' + window.CP_CODE + '/' + CP_SHEET_MAP[id] + '.png';
    return 'https://pelvicdashboard.com/api/clinician-portal/materials/' + id;
  }
  // Info sheets share as STOREFRONT links: the matching PDP opens with the
  // sheet PNG in an overlay (theme snippet tpp-sheet-overlay) and a Print
  // button; closing it reveals the product page. Patients land on
  // thepelvicpeople.com instead of a bare dashboard image URL.
  function _cpShareUrl(id) {
    var sheet = CP_SHEET_MAP[id];
    if (sheet) {
      var pdp = sheet.indexOf('kiwi') !== -1 ? '/products/kiwi' : '/products/ohnut-depth-limiting-rings';
      var url = 'https://thepelvicpeople.com' + pdp + '?sheet=' + sheet;
      if (window.CP_CODE) url += '&code=' + encodeURIComponent(window.CP_CODE);
      return url;
    }
    return cpMaterialUrl(id);
  }

  window.cpShareToggle = function (e, id, btn) {
    e.stopPropagation();
    $all('.cp-share-menu--open').forEach(function (el) {
      el.classList.remove('cp-share-menu--open');
      var b = el.parentElement.querySelector('[aria-haspopup]');
      if (b) b.setAttribute('aria-expanded', 'false');
    });
    var menu = btn.parentElement.querySelector('.cp-share-menu');
    menu.dataset.url = _cpShareUrl(id);
    var opened = !menu.classList.contains('cp-share-menu--open');
    menu.classList.toggle('cp-share-menu--open', opened);
    btn.setAttribute('aria-expanded', opened ? 'true' : 'false');
  };

  window.cpShareCopy = function (e, btn) {
    e.stopPropagation();
    var menu = btn.parentElement;
    navigator.clipboard.writeText(menu.dataset.url).then(function () {
      var orig = btn.innerHTML;
      btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg> Copied!';
      setTimeout(function () {
        btn.innerHTML = orig;
        menu.classList.remove('cp-share-menu--open');
        var hdr = menu.parentElement.querySelector('[aria-haspopup]');
        if (hdr) hdr.setAttribute('aria-expanded', 'false');
      }, 1200);
    });
  };

  window.cpShareEmail = function (e, btn) {
    e.stopPropagation();
    var menu = btn.parentElement;
    var url = menu.dataset.url;
    var title = menu.dataset.title || 'resource';
    var subject = 'Resource from The Pelvic People: ' + title;
    var body = 'Hi,\n\nSharing this resource from The Pelvic People:\n' + title + '\n\n' + url + '\n\nBest,';
    window.location.href = 'mailto:?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body);
    menu.classList.remove('cp-share-menu--open');
    var hdr = menu.parentElement.querySelector('[aria-haspopup]');
    if (hdr) hdr.setAttribute('aria-expanded', 'false');
  };

  document.addEventListener('click', function () {
    $all('.cp-share-menu--open').forEach(function (el) {
      el.classList.remove('cp-share-menu--open');
      var b = el.parentElement.querySelector('[aria-haspopup]');
      if (b) b.setAttribute('aria-expanded', 'false');
    });
  });

  window.cpDl = function (id, title, format) {
    var dlUrl = API + '/materials/' + id + '?download=1' + (token ? '&token=' + token : '');
    window.open(dlUrl, '_blank');
    if (title) {
      var viewed = [];
      try { viewed = JSON.parse(localStorage.getItem('cp_viewed_resources') || '[]'); } catch (e) {}
      viewed = viewed.filter(function (r) { return r.id !== id; });
      viewed.unshift({ id: id, title: title, format: format || 'PDF' });
      if (viewed.length > 5) viewed = viewed.slice(0, 5);
      localStorage.setItem('cp_viewed_resources', JSON.stringify(viewed));
      renderRecentResources();
    }
  };

  /* ═══ VIDEO LIBRARY (cards rendered by Liquid; JS adds collapse + modal) ═══ */
  var videosInited = false;
  var currentVideo = null;

  function initVideos() {
    if (videosInited) return;
    videosInited = true;
    $all('[data-cp-videos]').forEach(function (sec) {
      var grid = $('[data-cp-vid-grid]', sec);
      var more = $('[data-cp-vid-more]', sec);
      var initial = parseInt(sec.getAttribute('data-cp-initial') || '5', 10);
      var cards = $all('[data-cp-video]', grid);
      if (cards.length > initial) {
        grid.classList.add('cp-vid-grid--collapsed');
        if (more) {
          more.classList.remove('cp-hidden');
          setMoreLabel(more, false, cards.length);
        }
      } else if (more) {
        more.classList.add('cp-hidden');
      }
    });

    // Deep link ?video={id}
    var vid = new URLSearchParams(window.location.search).get('video');
    if (vid) {
      var card = $('[data-cp-video][data-vid="' + vid + '"]');
      if (card) {
        setTimeout(function () { openVideo(vid, card.getAttribute('data-title') || ''); }, 300);
      }
    }
  }

  function setMoreLabel(btn, open, total) {
    btn.innerHTML = (open ? 'Show less' : 'Show all ' + total + ' videos')
      + ' <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>';
    btn.classList.toggle('cp-vid-more--open', open);
  }

  function openVideo(id, title) {
    currentVideo = { id: id, title: title };
    var playerEl = document.getElementById('cp-vid-player');
    playerEl.innerHTML = '<iframe src="https://player.vimeo.com/video/' + id + '?autoplay=1&title=0&byline=0&portrait=0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>';
    document.getElementById('cp-vid-modal-title').textContent = title;
    document.getElementById('cp-vid-modal').classList.add('cp-vid-modal--open');
    document.body.style.overflow = 'hidden';
  }

  window.cpCloseVideo = function (e) {
    if (e && e.target !== e.currentTarget) return;
    document.getElementById('cp-vid-modal').classList.remove('cp-vid-modal--open');
    document.body.style.overflow = '';
    setTimeout(function () {
      document.getElementById('cp-vid-player').innerHTML = '';
      currentVideo = null;
    }, 300);
  };

  window.cpShareVideo = function () {
    if (!currentVideo) return;
    var url = window.location.origin + window.location.pathname + '?video=' + currentVideo.id;
    navigator.clipboard.writeText(url).then(function () {
      showToast('Link copied to clipboard');
      var btn = document.getElementById('cp-vid-modal-share');
      var orig = btn.innerHTML;
      btn.innerHTML = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6L9 17l-5-5"/></svg> Copied!';
      setTimeout(function () { btn.innerHTML = orig; }, 2000);
    });
  };

  function copyVideoLink(id, btn) {
    var url = window.location.origin + window.location.pathname + '?video=' + id;
    navigator.clipboard.writeText(url).then(function () {
      showToast('Link copied to clipboard');
      btn.textContent = 'Copied!';
      setTimeout(function () { btn.textContent = 'Copy link'; }, 2000);
    });
  }

  function showToast(msg) {
    var toast = document.getElementById('cp-toast');
    if (!toast) return;
    toast.textContent = msg;
    toast.classList.add('cp-toast--show');
    setTimeout(function () { toast.classList.remove('cp-toast--show'); }, 2500);
  }

  /* ═══ ORDER MATERIALS ═══ */
  function orderItems() {
    return $all('[data-cp-order-item]').map(function (el) {
      return {
        el: el,
        id: el.getAttribute('data-item-id'),
        variant_id: parseInt(el.getAttribute('data-variant-id') || '0', 10),
        max: parseInt(el.getAttribute('data-max') || '1', 10),
        qty_mult: parseInt(el.getAttribute('data-qty-mult') || '1', 10),
        type: el.getAttribute('data-item-type') || 'samples',
      };
    });
  }

  // Product images link to the portal-specific info pages (new tab); the
  // postcards card has no info page. Keyed by Shopify variant id — the one
  // stable identifier on the section's order blocks.
  var ORDER_INFO_PAGES = {
    40803073359970: '/pages/cp-product-kiwi',
    40354409709666: '/pages/cp-product-ohnut',
    52992826966388: '/pages/cp-product-lube',
  };

  // Order-summary pack sizes — fallback for templates that predate the
  // blocks' unit_count setting (the setting wins when present).
  // 1 postcard pack = 25 postcards; 1 lube pack = 25 single-use foils.
  var ORDER_UNIT_FALLBACK = { 30500014555234: 25, 52992826966388: 25 };

  function renderOrder() {
    var items = orderItems();
    if (!items.length) return;
    items.forEach(function (item) {
      cart[item.id] = cart[item.id] || 0;
      updateItemUi(item);
      var imgEl = item.el.querySelector('.cp-order-card__img');
      // The block's "Product info page" setting (data-info-url) wins; the
      // variant-id map is a fallback for docs that predate the setting.
      var infoUrl = (imgEl && imgEl.getAttribute('data-info-url')) || ORDER_INFO_PAGES[item.variant_id];
      if (infoUrl && imgEl && !imgEl.hasAttribute('data-cp-info-wired')) {
        imgEl.setAttribute('data-cp-info-wired', '1');
        imgEl.style.cursor = 'pointer';
        imgEl.setAttribute('role', 'link');
        imgEl.setAttribute('title', 'View product info');
        imgEl.addEventListener('click', function () { window.open(infoUrl, '_blank', 'noopener'); });
        // Visible affordance — "Product info ↗" pill in the image corner
        if (!document.getElementById('cp-order-info-css')) {
          var st = document.createElement('style');
          st.id = 'cp-order-info-css';
          st.textContent = '.cp .cp-order-card__info{position:absolute;bottom:10px;right:10px;z-index:1;display:inline-flex;align-items:center;gap:5px;padding:4px 11px;background:rgba(255,255,255,.92);border:1px solid rgba(41,47,47,.18);border-radius:999px;font-size:12px;font-weight:600;color:var(--ink);pointer-events:none;transition:background .2s;}'
            + '.cp .cp-order-card__img[data-cp-info-wired]:hover .cp-order-card__info{background:var(--btn-bg);}';
          document.head.appendChild(st);
        }
        if (!imgEl.querySelector('.cp-order-card__info')) {
          var badge = document.createElement('span');
          badge.className = 'cp-order-card__info';
          badge.innerHTML = 'Product info<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/></svg>';
          imgEl.appendChild(badge);
        }
      }
    });

    updateSummary();

    if (!isLoggedIn) {
      $all('[data-cp-qty]').forEach(function (b) { b.disabled = true; b.style.opacity = '0.4'; });
      var gate = $('[data-cp-order-gate]');
      if (gate && !gate.hasAttribute('data-cp-guested')) {
        gate.setAttribute('data-cp-guested', '1');
        gate.style.opacity = '0.35';
        gate.style.pointerEvents = 'none';
        gate.style.filter = 'grayscale(0.5)';
        var cta = document.createElement('div');
        cta.style.cssText = 'text-align:center;padding:24px 16px;margin-top:8px;background:var(--sand);border-radius:12px;border:1px dashed var(--border);';
        cta.innerHTML = wrongAccount() ? wrongAccountCta('order')
          : orderSigninHeading()
          + '<p style="color:var(--ink);margin:0 0 12px;">Sign in to order materials for your practice.</p>'
          + '<a href="/customer_authentication/login?return_to=' + encodeURIComponent(window.location.pathname) + '" onclick="try{sessionStorage.setItem(\'cp_return_tab\',\'order\')}catch(e){}" style="display:inline-block;padding:10px 28px;background:var(--btn-bg);color:var(--ink);border-radius:8px;font-weight:600;text-decoration:none;">Sign in</a>';
        gate.parentNode.insertBefore(cta, gate);
      }
      return;
    }

    var a = data.saved_address;
    if (a) {
      sv('name', a.name || ((a.first_name || '') + ' ' + (a.last_name || '')).trim());
      sv('addr', a.address1 || '');
      sv('addr2', a.address2 || '');
      sv('city', a.city || '');
      sv('state', a.province || '');
      sv('zip', a.zip || '');
      sv('country', a.country_code || a.country || 'US');
      sv('phone', a.phone || '');
    } else if (data.profile) {
      sv('name', data.profile.name || '');
      sv('city', data.profile.city || '');
      sv('state', data.profile.state || '');
    }
  }

  function sv(key, v) { var e = $('[data-cp-addr="' + key + '"]'); if (e) e.value = v; }
  function gv(key) { var e = $('[data-cp-addr="' + key + '"]'); return e ? e.value : ''; }

  function updateItemUi(item) {
    var q = cart[item.id] || 0;
    var val = $('[data-cp-qty-val]', item.el);
    if (val) val.textContent = q;
    var minus = $('[data-cp-qty="-1"]', item.el);
    var plus = $('[data-cp-qty="1"]', item.el);
    if (minus) minus.disabled = (q <= 0);
    if (plus) plus.disabled = (q >= item.max);
    item.el.classList.toggle('cp-order-card--selected', q > 0);
  }

  function stepQty(itemEl, d) {
    var item = orderItems().filter(function (i) { return i.el === itemEl; })[0];
    if (!item) return;
    cart[item.id] = Math.max(0, Math.min(item.max, (cart[item.id] || 0) + d));
    updateItemUi(item);
    updateSummary();
  }

  // Order summary panel: rebuild the item lines from the cart, pick the
  // ships-when wording, and reveal the panel once anything is selected.
  // Packs expand to units via the block's unit_count/unit_label settings
  // (4 postcard packs -> "100 postcards"); items without them list by name.
  function updateSummary() {
    var sum = $('[data-cp-order-summary]');
    if (!sum) return;
    var total = 0, rows = [];
    orderItems().forEach(function (item) {
      var q = cart[item.id] || 0;
      if (q <= 0) return;
      total += q;
      var unitCount = parseInt(item.el.getAttribute('data-unit-count') || '0', 10)
        || ORDER_UNIT_FALLBACK[item.variant_id] || 1;
      var nameEl = item.el.querySelector('.cp-order-card__name');
      var name = nameEl ? nameEl.textContent.trim() : 'Item';
      var imgEl = item.el.querySelector('.cp-order-card__img img');
      var detail = unitCount > 1
        ? (q + ' pack' + (q === 1 ? '' : 's') + ' \u00b7 ' + (q * unitCount) + ' units')
        : ('\u00d7 ' + q);
      rows.push({ name: name, detail: detail, src: imgEl ? (imgEl.currentSrc || imgEl.src) : '', alt: imgEl ? (imgEl.alt || name) : name });
    });
    var list = $('[data-cp-sum-items]', sum);
    if (list) {
      list.innerHTML = '';
      rows.forEach(function (r) {
        var li = document.createElement('li');
        var pic = document.createElement('div');
        pic.className = 'cp-sum-row__img' + (r.src ? '' : ' cp-sum-row__img--empty');
        if (r.src) {
          var im = document.createElement('img');
          im.src = r.src; im.alt = r.alt; im.loading = 'lazy';
          pic.appendChild(im);
        }
        var body = document.createElement('div');
        body.className = 'cp-sum-row__body';
        var nm = document.createElement('div'); nm.className = 'cp-sum-row__name'; nm.textContent = r.name;
        var dt = document.createElement('div'); dt.className = 'cp-sum-row__detail'; dt.textContent = r.detail;
        body.appendChild(nm); body.appendChild(dt);
        li.appendChild(pic); li.appendChild(body);
        list.appendChild(li);
      });
    }
    var eta = $('[data-cp-sum-eta]', sum);
    if (eta) {
      var tmr = new Date();
      tmr.setDate(tmr.getDate() + 1);
      var biz = tmr.getDay() >= 1 && tmr.getDay() <= 5;
      eta.textContent = biz
        ? (sum.getAttribute('data-eta-tomorrow') || 'Ships tomorrow')
        : (sum.getAttribute('data-eta-next') || 'Ships next business day');
    }
    sum.classList.toggle('cp-sum--on', total > 0);
  }

  function placeOrder() {
    var btn = $('[data-cp-order-btn]');
    var msg = $('[data-cp-order-msg]');
    if (msg) msg.innerHTML = '';

    var items = [], ot = null;
    var all = orderItems();
    all.forEach(function (item) {
      var q = cart[item.id] || 0;
      if (q > 0 && item.variant_id) {
        items.push({ variant_id: item.variant_id, quantity: q * (item.qty_mult || 1) });
        if (ot !== 'samples') ot = item.type;
      }
    });

    if (!items.length) { if (msg) msg.innerHTML = '<p class="cp-order-error">Select at least one item</p>'; return; }

    var addr = {
      first_name: gv('name').split(' ')[0],
      last_name: gv('name').split(' ').slice(1).join(' '),
      name: gv('name'),
      address1: gv('addr'),
      city: gv('city'),
      province: gv('state'),
      zip: gv('zip'),
      country_code: gv('country'),
      country: gv('country'),
      phone: gv('phone').trim(),
    };
    // Line 2 (apt, suite) rides to Shopify as address2. Shippo prints it as
    // its own line; ShipBob's import appends it to street line 1. Blank = left off.
    var addr2 = gv('addr2').trim();
    if (addr2) addr.address2 = addr2;
    if (!addr.address1 || !addr.city || !addr.zip) { if (msg) msg.innerHTML = '<p class="cp-order-error">Fill in your shipping address</p>'; return; }
    if (addr.phone.replace(/\D/g, '').length < 7) { if (msg) msg.innerHTML = '<p class="cp-order-error">Add a phone number</p>'; return; }

    btn.disabled = true; btn.textContent = 'Placing order…';

    api('/order', { method: 'POST', body: JSON.stringify({ order_type: ot, items: items, shipping_address: addr }) })
      .then(function (d) {
        if (msg) msg.innerHTML = '<div class="cp-order-success">' + d.message + (d.order_number ? ' · ' + d.order_number : '') + '</div>';
        return api('/me');
      })
      .then(function (d) {
        data = d;
        renderDashboard();
        all.forEach(function (item) { cart[item.id] = 0; updateItemUi(item); });
        updateSummary();
      })
      .catch(function (e) { if (msg) msg.innerHTML = '<p class="cp-order-error">' + e.message + '</p>'; })
      .finally(function () { btn.disabled = false; btn.textContent = btn.getAttribute('data-label') || 'Place Order'; });
  }

  /* ═══ PRODUCT INFO ═══ */
  // The Ohnut clinical reference document — fallback ONLY, shown when the
  // /pages/cp-product-ohnut embed fetch fails. Actively-maintained product
  // copy lives on the standalone pages and in the section blocks.
  var RICH_FALLBACK = {
    ohnut: {
      lead: 'Individuals experiencing deep dyspareunia, for any number of underlying causes, often feel lost, broken, or ashamed long before they come to you. It takes significant bravery to make that appointment and show up. It is our job to honor that bravery, and when applicable, offer tools that are simple to use and set them up for success.',
      whatItIs: {
        body: 'Ohnut is a set of stretchy (very stretchy), stackable rings worn around the base of a penetrating partner — whether that be penis, dilator, or toy. It limits insertion depth without limiting sensation. Rings can be added or removed at any time.',
        explainer: 'Ohnut limits penetration depth in a way that feels good for both partners.',
      },
      clinical: {
        intro: 'Ohnut may be appropriate for patients experiencing deep or collision dyspareunia related to:',
        groups: [
          { label: 'Gynecologic conditions', text: 'Endometriosis, adenomyosis, fibroids, PCOS/PMOS, pelvic organ prolapse, IC / painful bladder syndrome, menopause-related tissue changes.' },
          { label: 'Surgical recovery', text: 'Hysterectomy, gender-affirming vaginoplasty, abdominopelvic surgery, cancer treatment.' },
          { label: 'Functional / musculoskeletal', text: 'Pelvic floor dysfunction, postpartum recovery, scar tissue and adhesions.' },
          { label: 'Neurologic / psychogenic', text: 'PTSD, hypertonic pelvic floor secondary to trauma.' },
        ],
        help: {
          intro: 'By limiting depth, Ohnut may help:',
          items: [
            'Reduce pressure on tender or inflamed structures',
            'Decrease tension on scar tissue or adhesions',
            'Downregulate the nervous system sufficiently to allow for pelvic floor relaxation',
          ],
        },
        footer: 'Dyspareunia often has complex underlying causes that require a multidisciplinary team to address. Ohnut allows patients to maintain intimacy while that work is underway.',
      },
      tips: [
        { quote: 'Start with 3 or 4 rings, not 1.', text: 'Starting with one ring and needing more can feel like failure. Starting with three or four and taking rings off doesn’t. Start with more rings. We also recommend reinforcing that the rings are not a progression — depth tolerance varies by position, cycle, and day. The number of rings used is not a metric of success.' },
        { quote: 'It feels like going all the way in.', text: 'Reframe partner concerns proactively. Most patients are already worried about being a burden. Get ahead of it: “For your partner, it feels like going all the way in without worrying about going too far.”' },
        { quote: 'It sparks conversation.', text: 'Patients who previously responded to pain with silence, guarding, or shutting down now have language around a neutral object: “Want to add a ring? Take one off? How does this feel?” That verbal shift toward establishing trust and safety is critical.' },
      ],
      vibrating: 'Stacks anywhere on the stack and can be rotated for either partner’s sensation. Vibration is useful for increasing blood flow and oxygen to surrounding muscles, encourages relaxation, and invites exploratory enjoyment for both partners.',
      notes: [
        'HSA / FSA eligible.',
        'Lube is non-negotiable. Water-based or silicone-based — both work with Ohnut’s body-safe polymer blend material (not pure silicone, which isn’t stretchy enough).',
        'Menopause / ED tip: if a partner is concerned about erection loss, suggest they try Ohnut alone first to get used to the feel before bringing it into shared intimacy.',
        'Cleaning: soap and water. Fragrance-free preferred.',
      ],
    },
  };

  function productFromCard(el) {
    function lines(attr) {
      return (el.getAttribute(attr) || '').split('\n').map(function (s) { return s.trim(); }).filter(Boolean);
    }
    return {
      id: el.getAttribute('data-key') || '',
      product_key: el.getAttribute('data-product-key') || el.getAttribute('data-key') || '',
      embed: el.getAttribute('data-embed') || '',
      name: el.getAttribute('data-name') || '',
      tagline: el.getAttribute('data-tagline') || '',
      image: el.getAttribute('data-image') || '',
      images: lines('data-images'),
      uses: lines('data-uses'),
      body: el.getAttribute('data-body') || '',
      how_to_use: el.getAttribute('data-how') || '',
      note: el.getAttribute('data-note') || '',
      url: el.getAttribute('data-url') || '',
      rich: RICH_FALLBACK[el.getAttribute('data-key')] || null,
    };
  }

  var _cpCarousel = { index: 0, total: 0 };

  function _cpBuildCarousel(prod) {
    var imgs = (prod.images && prod.images.length) ? prod.images : [prod.image];
    _cpCarousel = { index: 0, total: imgs.length };
    var slides = imgs.map(function (src, i) {
      return '<div class="cp-carousel__slide"><img src="' + src + '" alt="' + prod.name + ' — image ' + (i + 1) + '" draggable="false" loading="lazy"></div>';
    }).join('');
    var thumbsHtml = '';
    if (imgs.length > 1) {
      var thumbs = imgs.map(function (src, i) {
        return '<div class="cp-thumb' + (i === 0 ? ' cp-thumb--active' : '') + '" onclick="cpCarouselGo(' + i + ')" role="button" tabindex="0"><img src="' + src + '" alt="" draggable="false"></div>';
      }).join('');
      thumbsHtml = '<div class="cp-thumbs">' + thumbs + '</div>';
    }
    return '<div class="cp-carousel">'
      + '<div class="cp-carousel__track" id="cp-carousel-track" style="transform:translateX(0%)">' + slides + '</div>'
      + '</div>'
      + thumbsHtml;
  }

  function _cpUpdateCarousel(animate) {
    var track = document.getElementById('cp-carousel-track');
    if (track) {
      track.style.transition = animate === false ? 'none' : '';
      track.style.transform = 'translateX(-' + (_cpCarousel.index * 100) + '%)';
    }
    $all('.cp-thumb').forEach(function (d, i) {
      d.classList.toggle('cp-thumb--active', i === _cpCarousel.index);
    });
  }

  function _cpAttachSwipe() {
    var car = $('.cp-carousel');
    var track = document.getElementById('cp-carousel-track');
    if (!car || !track || _cpCarousel.total < 2) return;
    var startX = 0, startY = 0, width = 0, dragging = false, horizontal = false, pointerId = null;
    var SLOP = 8;
    var THRESHOLD = 0.15;

    function onDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      pointerId = e.pointerId;
      startX = e.clientX; startY = e.clientY;
      width = car.getBoundingClientRect().width;
      dragging = true; horizontal = false;
      track.style.transition = 'none';
    }
    function onMove(e) {
      if (!dragging || e.pointerId !== pointerId) return;
      var dx = e.clientX - startX;
      var dy = e.clientY - startY;
      if (!horizontal) {
        if (Math.abs(dx) < SLOP && Math.abs(dy) < SLOP) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          dragging = false;
          track.style.transition = '';
          return;
        }
        horizontal = true;
        try { car.setPointerCapture(pointerId); } catch (_) {}
      }
      if (e.cancelable) e.preventDefault();
      var pct = (dx / width) * 100;
      track.style.transform = 'translateX(calc(' + (-_cpCarousel.index * 100) + '% + ' + pct + '%))';
    }
    function onUp(e) {
      if (!dragging || (pointerId != null && e.pointerId !== pointerId)) return;
      dragging = false;
      try { car.releasePointerCapture(pointerId); } catch (_) {}
      track.style.transition = '';
      if (!horizontal) return;
      var dx = e.clientX - startX;
      if (dx > width * THRESHOLD) {
        _cpCarousel.index = (_cpCarousel.index - 1 + _cpCarousel.total) % _cpCarousel.total;
      } else if (dx < -width * THRESHOLD) {
        _cpCarousel.index = (_cpCarousel.index + 1) % _cpCarousel.total;
      }
      _cpUpdateCarousel();
    }

    car.addEventListener('pointerdown', onDown);
    car.addEventListener('pointermove', onMove);
    car.addEventListener('pointerup', onUp);
    car.addEventListener('pointercancel', onUp);
    car.addEventListener('dragstart', function (e) { e.preventDefault(); });
  }

  window.cpCarouselGo = function (i) {
    _cpCarousel.index = i;
    _cpUpdateCarousel();
  };

  function _cpEscapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function _cpRenderReviews(product_key) {
    var host = document.getElementById('cp-reviews-list');
    if (!host) return;
    host.innerHTML = '<div class="cp-reviews__loading">Loading patient reviews…</div>';
    fetch(API + '/reviews?product=' + encodeURIComponent(product_key) + '&limit=6', {
      headers: { 'Content-Type': 'application/json' },
    }).then(function (r) { return r.json(); }).then(function (d) {
      var host2 = document.getElementById('cp-reviews-list');
      if (!host2) return;
      var revs = (d && d.reviews) || [];
      if (!revs.length) {
        host2.innerHTML = '<div class="cp-reviews__empty">No reviews yet for this product.</div>';
        return;
      }
      host2.innerHTML = revs.map(function (r) {
        var stars = '★★★★★'.slice(0, r.rating);
        var verified = r.verified_buyer ? '<span class="cp-review__verified">'
          + '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>'
          + 'Verified</span>' : '';
        return '<div class="cp-review">'
          + '<div class="cp-review__stars">' + stars + '</div>'
          + (r.title ? '<div class="cp-review__title">' + _cpEscapeHtml(r.title) + '</div>' : '')
          + '<div class="cp-review__body">' + _cpEscapeHtml(r.body) + '</div>'
          + '<div class="cp-review__meta">'
          + _cpEscapeHtml(r.reviewer_name) + (r.date_formatted ? ' · ' + _cpEscapeHtml(r.date_formatted) : '')
          + (verified ? ' · ' + verified : '')
          + '</div>'
          + '</div>';
      }).join('');
    }).catch(function () {
      var host2 = document.getElementById('cp-reviews-list');
      if (host2) host2.innerHTML = '<div class="cp-reviews__empty">Reviews are temporarily unavailable.</div>';
    });
  }

  var _cpViewBtn = function (prod) {
    return '<a href="' + prod.url + '" target="_blank" rel="noopener noreferrer" class="cp-btn" style="display:inline-flex;align-items:center;gap:6px;margin-top:20px;padding:10px 18px;background:var(--btn-bg);color:var(--ink);border-radius:8px;font-weight:600;">'
      + 'View on thepelvicpeople.com'
      + '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17L17 7M9 7h8v8"/></svg>'
      + '</a>';
  };

  function _cpBuildRichContent(prod) {
    var r = prod.rich;
    var groupsHtml = r.clinical.groups.map(function (g) {
      return '<div class="cp-prod-group"><div class="cp-prod-group__label">' + g.label + '</div>'
        + '<div class="cp-prod-group__text">' + g.text + '</div></div>';
    }).join('');
    var helpItems = r.clinical.help.items.map(function (i) { return '<li>' + i + '</li>'; }).join('');
    var tipsHtml = r.tips.map(function (t, i) {
      return '<div class="cp-prod-tip">'
        + '<div class="cp-prod-tip__num">' + (i + 1) + '</div>'
        + '<div class="cp-prod-tip__main">'
        + '<div class="cp-prod-tip__quote">“' + t.quote + '”</div>'
        + '<div class="cp-prod-tip__text">' + t.text + '</div>'
        + '</div></div>';
    }).join('');
    var notesHtml = r.notes.map(function (n) { return '<li>' + n + '</li>'; }).join('');

    var rich = '<div class="cp-prod-rich">'
      + '<section class="cp-prod-rich__section">'
      + '<h3>What it is</h3>'
      + '<p>' + r.whatItIs.body + '</p>'
      + '<div class="cp-prod-quote">“' + r.whatItIs.explainer + '”</div>'
      + '</section>'
      + '<section class="cp-prod-rich__section">'
      + '<h3>Clinical applications</h3>'
      + '<p>' + r.clinical.intro + '</p>'
      + '<div class="cp-prod-groups">' + groupsHtml + '</div>'
      + '<div class="cp-prod-highlight"><div class="cp-prod-highlight__intro">' + r.clinical.help.intro + '</div>'
      + '<ul>' + helpItems + '</ul></div>'
      + '<p>' + r.clinical.footer + '</p>'
      + '</section>'
      + '<section class="cp-prod-rich__section">'
      + '<h3>Three things to tell every patient</h3>'
      + '<div class="cp-prod-tips">' + tipsHtml + '</div>'
      + '</section>'
      + '<section class="cp-prod-rich__section">'
      + '<h3>Vibrating add-on ring</h3>'
      + '<p>' + r.vibrating + '</p>'
      + '</section>'
      + '<section class="cp-prod-rich__section">'
      + '<h3>Practical notes</h3>'
      + '<ul class="cp-prod-notes">' + notesHtml + '</ul>'
      + '</section>'
      + '</div>';

    return '<div class="cp-prod-detail">'
      + '<div class="cp-prod-detail__content">'
      + '<h2 class="cp-prod-detail__name">' + prod.name + '</h2>'
      + '<p class="cp-prod-detail__tagline">' + prod.tagline + '</p>'
      + '<p class="cp-prod-rich__lead">' + r.lead + '</p>'
      + _cpViewBtn(prod)
      + '</div>'
      + '<div class="cp-prod-detail__media">' + _cpBuildCarousel(prod) + '</div>'
      + '</div>'
      + rich;
  }

  function _cpBuildSimpleContent(prod) {
    var usesHtml = '';
    (prod.uses || []).forEach(function (u) { usesHtml += '<li>' + u + '</li>'; });
    return '<div class="cp-prod-detail">'
      + '<div class="cp-prod-detail__content">'
      + '<h2 class="cp-prod-detail__name">' + prod.name + '</h2>'
      + '<p class="cp-prod-detail__tagline">' + prod.tagline + '</p>'
      + (usesHtml ? '<div style="font-weight:600;color:var(--ochre);text-transform:uppercase;letter-spacing:0.04em;margin-bottom:8px;">Commonly recommended for</div>'
        + '<ul class="cp-prod-detail__uses">' + usesHtml + '</ul>' : '')
      + '<p class="cp-prod-detail__body">' + prod.body + '</p>'
      + (prod.how_to_use ? '<div style="font-weight:600;color:var(--ochre);text-transform:uppercase;letter-spacing:0.04em;margin:20px 0 8px;">How patients use it</div>'
        + '<p class="cp-prod-detail__body" style="margin-bottom:0;">' + prod.how_to_use + '</p>' : '')
      + (prod.note ? '<p style="color:var(--ochre);font-weight:500;margin-top:16px;">⚠ ' + prod.note + '</p>' : '')
      + _cpViewBtn(prod)
      + '</div>'
      + '<div class="cp-prod-detail__media">' + _cpBuildCarousel(prod) + '</div>'
      + '</div>';
  }

  /* ─── Embedded product pages (fetch + inject, theme CSS applies) ─── */
  var _cpEmbedCache = {};

  function _cpEmbedBackBar() {
    return '<div style="max-width:1200px;margin:0 auto;padding:20px 24px 0;">'
      + '<button onclick="cpBackToProducts()" style="display:inline-flex;align-items:center;gap:6px;background:none;border:none;cursor:pointer;font-weight:600;color:#4a4f4f;padding:0;font-size:15px;min-width:0;width:auto;">'
      + '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>'
      + 'Back to products</button></div>';
  }

  function _cpInitThemeSections(host) {
    if (!window.jQuery) return;
    host.querySelectorAll('.shopify-section').forEach(function (sec) {
      try { jQuery(document).trigger(jQuery.Event('shopify:section:load', { target: sec })); } catch (e) {}
    });
  }

  // The shell's "Back to website" link is hidden while a product page is
  // shown (embedded page OR built-in detail view) (the shell CSS pins it display:inline-flex !important, so the
  // override must be !important too) and restored on every way out.
  function _cpTopbarBack(show) {
    $all('.cp-topbar__back').forEach(function (el) {
      if (show) el.style.removeProperty('display');
      else el.style.setProperty('display', 'none', 'important');
    });
  }

  function cpShowEmbed(prod) {
    var host = $('[data-cp-embed-host]');
    _cpTopbarBack(false);
    $all('[data-cp-prod-list]').forEach(function (el) { el.classList.add('cp-hidden'); });
    $all('[data-cp-prod-detail]').forEach(function (el) { el.classList.add('cp-hidden'); });
    host.classList.remove('cp-hidden');

    if (_cpEmbedCache[prod.id]) {
      host.innerHTML = _cpEmbedBackBar() + _cpEmbedCache[prod.id];
      _cpInitThemeSections(host);
      window.scrollTo(0, 0);
      return;
    }

    host.innerHTML = _cpEmbedBackBar() + '<p style="text-align:center;padding:80px 0;color:#8a8f8f;">Loading…</p>';
    window.scrollTo(0, 0);

    fetch(prod.embed)
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var main = doc.getElementById('MainContent');
        if (!main) throw new Error('no MainContent');
        // The page's own "Back to portal" links (hand-coded .cpp-hero__back,
        // Studio ps- sections' a.ps-back / back_label) — the embed's own
        // "Back to products" bar is the only way back inside the portal.
        main.querySelectorAll('.cpp-hero__back, .ps-back, [data-ps-setting="back_label"]').forEach(function (bk) {
          var a = bk.closest('a, button') || bk;
          if (a.parentNode) a.parentNode.removeChild(a);
        });
        var scripts = [];
        main.querySelectorAll('script').forEach(function (s) {
          if (!s.hasAttribute('data-locksmith')) scripts.push({ src: s.getAttribute('src'), text: s.textContent });
          s.remove();
        });
        _cpEmbedCache[prod.id] = main.innerHTML;
        host.innerHTML = _cpEmbedBackBar() + main.innerHTML;
        scripts.forEach(function (sp) {
          var el = document.createElement('script');
          if (sp.src) { el.src = sp.src; } else { el.textContent = sp.text; }
          document.body.appendChild(el);
        });
        _cpInitThemeSections(host);
      })
      .catch(function () {
        host.classList.add('cp-hidden');
        _cpShowLegacyProduct(prod);
      });
  }

  function showProduct(cardEl) {
    var prod = productFromCard(cardEl);
    if (prod.embed) { cpShowEmbed(prod); return; }
    _cpShowLegacyProduct(prod);
  }

  function _cpShowLegacyProduct(prod) {
    var detailEl = $('[data-cp-prod-detail]');
    if (!detailEl) return;
    _cpTopbarBack(false); // no "Back to website" on ANY product view, embed or built-in
    detailEl.innerHTML = '<button class="cp-prod-detail__back" onclick="cpBackToProducts()">'
      + '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>'
      + 'Back to products</button>'
      + (prod.rich && !prod.body ? _cpBuildRichContent(prod) : _cpBuildSimpleContent(prod))
      + '<div class="cp-reviews">'
      + '<div class="cp-reviews__header">'
      + '<div class="cp-reviews__title">What patients are saying</div>'
      + '<div class="cp-reviews__subtitle">Curated 5-star reviews from the last 12 months</div>'
      + '</div>'
      + '<div class="cp-reviews__list" id="cp-reviews-list"></div>'
      + '</div>';

    $all('[data-cp-prod-list]').forEach(function (el) { el.classList.add('cp-hidden'); });
    detailEl.classList.remove('cp-hidden');
    _cpAttachSwipe();
    _cpRenderReviews(prod.product_key || prod.id);
  }

  window.cpBackToProducts = function () {
    $all('[data-cp-prod-list]').forEach(function (el) { el.classList.remove('cp-hidden'); });
    $all('[data-cp-prod-detail]').forEach(function (el) { el.classList.add('cp-hidden'); });
    var emb = $('[data-cp-embed-host]');
    if (emb) emb.classList.add('cp-hidden');
    _cpTopbarBack(true);
    window.scrollTo(0, 0);
  };

  /* ─── Profile ─── */
  function renderProfile() {
    if (!isLoggedIn) {
      if (wrongAccount()) { wireSwitchLinks(); return; }
      var profBtn = $('[data-cp-account]');
      if (profBtn) {
        var loginHref = '/customer_authentication/login?return_to=' + encodeURIComponent(window.location.pathname + window.location.search);
        if (profBtn.classList.contains('cp-topbar__signin')) {
          // Studio shell renders the (editable, themed) Sign in button for guests — just wire it
          profBtn.setAttribute('href', loginHref);
          profBtn.onclick = function () { try { sessionStorage.setItem('cp_return_tab', (window.location.hash || '').replace('#', '')); } catch (e) {} };
        } else {
          profBtn.outerHTML = '<a href="' + loginHref + '" onclick="try{sessionStorage.setItem(\'cp_return_tab\',(window.location.hash||\'\').replace(\'#\',\'\'))}catch(e){}" class="cp-btn cp-topbar__signin" style="padding:5px 14px;background:var(--btn-bg);color:var(--ink);border-radius:8px;text-decoration:none;font-weight:600;">Sign in</a>';
        }
      }
      return;
    }
    var p = data.profile || {};
    // Signed in: the person icon becomes the clinician's initials (green chip)
    var acctBtn = $('[data-cp-account]');
    if (acctBtn && acctBtn.classList.contains('cp-topbar__signin')) {
      // Liquid rendered the guest Sign in link but the API says signed in —
      // swap in the account chip so the profile menu has its trigger
      var chip = document.createElement('button');
      chip.className = 'cp-topbar__icon';
      chip.setAttribute('data-cp-account', '');
      chip.title = 'Account';
      acctBtn.parentNode.replaceChild(chip, acctBtn);
      acctBtn = chip;
    }
    if (acctBtn) {
      var parts = (p.name || '').trim().split(/\s+/).filter(Boolean);
      var initials = parts.length > 1
        ? parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
        : (parts[0] || '').charAt(0);
      if (initials) {
        acctBtn.textContent = initials.toUpperCase();
        acctBtn.classList.add('cp-topbar__icon--user');
      }
    }
    var el = $('[data-cp-profile-info]');
    if (!el) return;
    var badges = '';
    if (p.vip) badges += ' <span class="cp-badge">✦ VIP</span>';
    if (p.raving_fan) badges += ' <span class="cp-badge">♥ Raving Fan</span>';
    var meta = p.profession || '';
    if (p.city && p.state) meta += (meta ? ' · ' : '') + p.city + ', ' + p.state;
    el.innerHTML = '<div style="font-family:Instrument Serif,Georgia,serif;font-style: normal;color:var(--plum);margin-bottom:4px;">' + (p.name || 'Clinician') + '</div>'
      + (badges ? '<div style="margin-bottom:6px;">' + badges + '</div>' : '')
      + '<div style="color:var(--ink-light);">' + meta + '</div>'
      + '<div style="color:var(--ink-faint);margin-top:4px;">' + (p.email || '') + '</div>';
  }

  /* ─────────────────────── Event delegation ─────────────────────── */
  document.addEventListener('click', function (e) {
    var t;
    if ((t = e.target.closest('[data-cp-nav]'))) { cpNav(t.getAttribute('data-cp-nav')); return; }
    if ((t = e.target.closest('[data-cp-qty]'))) {
      e.stopPropagation();
      var itemEl = t.closest('[data-cp-order-item]');
      if (itemEl && !t.disabled) stepQty(itemEl, parseInt(t.getAttribute('data-cp-qty'), 10));
      return;
    }
    if ((t = e.target.closest('[data-cp-order-btn]'))) { placeOrder(); return; }
    if ((t = e.target.closest('[data-cp-vid-copy]'))) {
      e.stopPropagation();
      var card0 = t.closest('[data-cp-video]');
      if (card0) copyVideoLink(card0.getAttribute('data-vid'), t);
      return;
    }
    if ((t = e.target.closest('[data-cp-video]'))) {
      openVideo(t.getAttribute('data-vid'), t.getAttribute('data-title') || '');
      return;
    }
    if ((t = e.target.closest('[data-cp-vid-more]'))) {
      var sec = t.closest('[data-cp-videos]');
      var grid = sec && $('[data-cp-vid-grid]', sec);
      if (grid) {
        var open = grid.classList.toggle('cp-vid-grid--collapsed') === false;
        setMoreLabel(t, open, $all('[data-cp-video]', grid).length);
      }
      return;
    }
    if ((t = e.target.closest('[data-cp-filter]'))) {
      var f = t.getAttribute('data-cp-filter');
      if (f === 'all') selectAllFilters();
      else toggleFilter(f, t);
      return;
    }
    if ((t = e.target.closest('[data-cp-product]'))) { showProduct(t); return; }
    if ((t = e.target.closest('[data-cp-account]'))) {
      // The guest Sign in link shares data-cp-account; let it navigate without
      // flashing the signed-in Sign out card
      if (!t.classList.contains('cp-topbar__signin')) toggleProfile();
      return;
    }
    if ((t = e.target.closest('[data-cp-signout]'))) { window.cpSignOut(); return; }
    if ((t = e.target.closest('[data-cp-profile]')) && e.target === t) { toggleProfile(); return; }
    // Navigate from action cards LAST so inner buttons above win
    if ((t = e.target.closest('[data-cp-goto]'))) { cpNav(t.getAttribute('data-cp-goto')); return; }
  });

  document.addEventListener('input', function (e) {
    if (e.target && e.target.matches && e.target.matches('[data-cp-res-search]')) cpFilterResources();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      window.cpPreviewClose();
      if (currentVideo) window.cpCloseVideo();
      $all('.cp-share-menu--open').forEach(function (el) { el.classList.remove('cp-share-menu--open'); });
    }
  });
  // Keyboard activation for product cards (role=button)
  document.addEventListener('keydown', function (e) {
    if ((e.key === 'Enter' || e.key === ' ') && e.target.closest && e.target.closest('[data-cp-product]')) {
      e.preventDefault();
      showProduct(e.target.closest('[data-cp-product]'));
    }
  });

  /* ── Standalone widgets: live review cards on any page (ps-cp-reviews) —
        no portal shell required. ── */
  function fillReviewWidgets() {
    $all('[data-cp-reviews]').forEach(function (host) {
      if (host.hasAttribute('data-cp-reviews-done')) return;
      host.setAttribute('data-cp-reviews-done', '1');
      var key = host.getAttribute('data-product-key') || 'ohnut';
      var limit = parseInt(host.getAttribute('data-limit') || '6', 10);
      fetch(API + '/reviews?product=' + encodeURIComponent(key) + '&limit=' + limit, {
        headers: { 'Content-Type': 'application/json' },
      }).then(function (r) { return r.json(); }).then(function (d) {
        var revs = (d && d.reviews) || [];
        host.innerHTML = revs.map(function (r) {
          var stars = '★★★★★'.slice(0, r.rating);
          var verified = r.verified_buyer ? '<span class="psr-verified">'
            + '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>'
            + 'Verified</span>' : '';
          return '<div class="psr-card">'
            + '<div class="psr-stars">' + stars + '</div>'
            + (r.title ? '<div class="psr-title">' + _cpEscapeHtml(r.title) + '</div>' : '')
            + '<div class="psr-body">' + _cpEscapeHtml(r.body) + '</div>'
            + '<div class="psr-meta">'
            + _cpEscapeHtml(r.reviewer_name) + (r.date_formatted ? ' · ' + _cpEscapeHtml(r.date_formatted) : '')
            + (verified ? ' · ' + verified : '')
            + '</div>'
            + '</div>';
        }).join('');
      }).catch(function () { /* placeholder stays */ });
    });
  }

  /* ─────────────────────── Init ─────────────────────── */
  function init() {
    fillReviewWidgets();
    shell = $('[data-cp-shell]');
    if (!shell) return;
    document.documentElement.classList.add('cp-js');
    buildChrome();
    EMAIL = shell.getAttribute('data-cp-email') || null;
    token = EMAIL ? localStorage.getItem('cp_token') : null;
    // A token saved for another account on this browser is not this clinician's.
    try {
      var tokEmail = localStorage.getItem('cp_token_email');
      if (token && tokEmail && tokEmail !== EMAIL.toLowerCase()) { token = null; localStorage.removeItem('cp_token'); }
    } catch (x) {}
    WRONG = EMAIL ? null : $('[data-cp-wrong]');
    if (WRONG) pingWrongAccount();

    if (EMAIL) {
      if (token) { show(); } else { authShopify(); }
    } else {
      show(); // browse-only mode
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
